// @ts-nocheck
/**
 * Pure color parsing and HSL adjustment helpers for expression callPure functions (DEC-047).
 */

export interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function toHexByte(value: number): string {
  return clampByte(value).toString(16).padStart(2, '0');
}

export function formatHexColor(r: number, g: number, b: number, a = 1): string {
  const hex = `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`;
  if (a >= 1) {
    return hex;
  }
  return `${hex}${toHexByte(a * 255)}`;
}

export function parseColorInput(color: string): RgbaColor | null {
  const raw = color.trim();
  if (!raw) {
    return null;
  }

  const hexMatch = /^#([0-9a-fA-F]{3,8})$/.exec(raw);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex.split('').map((char) => `${char}${char}`).join('');
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      if ([r, g, b, a].every((n) => Number.isFinite(n))) {
        return { r, g, b, a: clamp01(a) };
      }
    }
    return null;
  }

  const rgbMatch = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/.exec(raw);
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    const a = rgbMatch[4] !== undefined ? Number(rgbMatch[4]) : 1;
    if ([r, g, b, a].every((n) => Number.isFinite(n))) {
      return { r, g, b, a: clamp01(a) };
    }
  }

  return null;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      default:
        h = ((rn - gn) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s, l };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let rn = 0;
  let gn = 0;
  let bn = 0;

  if (hue < 60) {
    rn = c;
    gn = x;
  } else if (hue < 120) {
    rn = x;
    gn = c;
  } else if (hue < 180) {
    gn = c;
    bn = x;
  } else if (hue < 240) {
    gn = x;
    bn = c;
  } else if (hue < 300) {
    rn = x;
    bn = c;
  } else {
    rn = c;
    bn = x;
  }

  return {
    r: (rn + m) * 255,
    g: (gn + m) * 255,
    b: (bn + m) * 255,
  };
}

export function applyColorWithAlpha(color: string, alpha: number): string | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }
  return formatHexColor(parsed.r, parsed.g, parsed.b, clamp01(alpha));
}

export function adjustColorBrightness(color: string, amount: number): string | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }
  const hsl = rgbToHsl(parsed.r, parsed.g, parsed.b);
  const nextLightness = clamp01(hsl.l + amount);
  const rgb = hslToRgb(hsl.h, hsl.s, nextLightness);
  return formatHexColor(rgb.r, rgb.g, rgb.b, parsed.a);
}

export function adjustColorSaturation(color: string, amount: number): string | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }
  const hsl = rgbToHsl(parsed.r, parsed.g, parsed.b);
  const nextSaturation = clamp01(hsl.s + amount);
  const rgb = hslToRgb(hsl.h, nextSaturation, hsl.l);
  return formatHexColor(rgb.r, rgb.g, rgb.b, parsed.a);
}

export function shiftColorHue(color: string, degrees: number): string | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }
  const hsl = rgbToHsl(parsed.r, parsed.g, parsed.b);
  const rgb = hslToRgb(hsl.h + degrees, hsl.s, hsl.l);
  return formatHexColor(rgb.r, rgb.g, rgb.b, parsed.a);
}

export type ColorSpace = 'rgb' | 'hsl';

export type RgbChannel = 'r' | 'g' | 'b';

export type HslChannel = 'h' | 's' | 'l';

export type ColorSplitChannel = RgbChannel | HslChannel;

function hslHueToByte(h: number): number {
  return clampByte((h / 360) * 255);
}

function hslFractionToByte(fraction: number): number {
  return clampByte(fraction * 255);
}

function byteToHslHue(byte: number): number {
  return (clampByte(byte) / 255) * 360;
}

function byteToHslFraction(byte: number): number {
  return clampByte(byte) / 255;
}

function isRgbChannel(channel: ColorSplitChannel): channel is RgbChannel {
  return channel === 'r' || channel === 'g' || channel === 'b';
}

function isHslChannel(channel: ColorSplitChannel): channel is HslChannel {
  return channel === 'h' || channel === 's' || channel === 'l';
}

export function splitColorChannel(
  color: string,
  colorSpace: ColorSpace,
  channel: ColorSplitChannel,
): number | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }

  if (colorSpace === 'rgb') {
    if (!isRgbChannel(channel)) {
      return null;
    }
    if (channel === 'r') return clampByte(parsed.r);
    if (channel === 'g') return clampByte(parsed.g);
    return clampByte(parsed.b);
  }

  if (!isHslChannel(channel)) {
    return null;
  }

  const hsl = rgbToHsl(parsed.r, parsed.g, parsed.b);
  if (channel === 'h') return hslHueToByte(hsl.h);
  if (channel === 's') return hslFractionToByte(hsl.s);
  return hslFractionToByte(hsl.l);
}

export function mergeColorComponents(
  a: number,
  b: number,
  c: number,
  colorSpace: ColorSpace,
): string | null {
  if (![a, b, c].every((n) => Number.isFinite(n))) {
    return null;
  }

  if (colorSpace === 'rgb') {
    return formatHexColor(clampByte(a), clampByte(b), clampByte(c));
  }

  const rgb = hslToRgb(
    byteToHslHue(a),
    byteToHslFraction(b),
    byteToHslFraction(c),
  );
  return formatHexColor(rgb.r, rgb.g, rgb.b);
}

/**
 * Pack a hex/rgb color string to a 32-bit int as `0xRRGGBBAA` (alpha last, matching `#RRGGBBAA`).
 * Missing alpha in the input ⇒ opaque (`A = 0xFF`). Returns `null` when the color cannot be parsed.
 */
export function hexColorToInt(color: string): number | null {
  const parsed = parseColorInput(color);
  if (!parsed) {
    return null;
  }
  const aByte = clampByte(parsed.a * 255);
  // Use >>> 0 so the result is an unsigned 32-bit value (JS bitwise is signed).
  return (
    ((clampByte(parsed.r) << 24)
      | (clampByte(parsed.g) << 16)
      | (clampByte(parsed.b) << 8)
      | aByte)
    >>> 0
  );
}

/**
 * Unpack a 32-bit `0xRRGGBBAA` int to `#RRGGBB` (opaque) or `#RRGGBBAA`.
 * Returns `null` when `value` is not a finite number.
 */
export function intToHexColor(value: number): string | null {
  if (!Number.isFinite(value)) {
    return null;
  }
  const packed = value >>> 0;
  const r = (packed >>> 24) & 0xff;
  const g = (packed >>> 16) & 0xff;
  const b = (packed >>> 8) & 0xff;
  const a = packed & 0xff;
  return formatHexColor(r, g, b, a / 255);
}

/**
 * Pack one color or a list of colors. List in → list out; scalar in → scalar out.
 * Invalid entries become `null` (lists keep alignment).
 */
export function hexColorToIntAuto(
  color: string | readonly string[],
): number | null | Array<number | null> {
  if (Array.isArray(color)) {
    return color.map((entry) => hexColorToInt(String(entry ?? '')));
  }
  return hexColorToInt(String(color ?? ''));
}

/**
 * Unpack one packed int or a list of ints. List in → list out; scalar in → scalar out.
 * Invalid entries become `null` (lists keep alignment).
 */
export function intToHexColorAuto(
  value: number | readonly number[],
): string | null | Array<string | null> {
  if (Array.isArray(value)) {
    return value.map((entry) => intToHexColor(Number(entry)));
  }
  return intToHexColor(Number(value));
}
